import { lazy } from 'react';

/**
 * The screen chunks, and the ability to start one early.
 *
 * Screens are code-split: a fee clerk who never opens Timetable should not pay
 * to download it. v1 imported all 34 screens eagerly into one bundle.
 *
 * ── Why the loaders are named, rather than inlined into `lazy()` ──
 * A `lazy()` component only starts its download when React renders it, which is
 * *after* the click. The chunk fetch then sits between the click and the first
 * pixel of the new screen, and the user is looking at the old one for the whole
 * of it. Keeping each `import()` as a callable here lets the sidebar start the
 * same download on hover — a pointer travels to a nav item a few hundred
 * milliseconds before the button goes down, which is most of what a chunk of
 * this size needs. By the time the click lands there is usually nothing to wait
 * for.
 *
 * A nav entry without a loader here renders the "not built yet" placeholder,
 * which is how the remaining v1 pages appear until each is ported — visible in
 * the sidebar, honest about its state, and impossible to forget.
 */

type ScreenModule = { default: () => React.JSX.Element };

const loaders: Record<string, () => Promise<ScreenModule>> = {
  dashboard: () => import('@/pages/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })),
  students: () =>
    import('@/pages/students/StudentListing').then((m) => ({ default: m.StudentListing })),
  staff: () => import('@/pages/staff/StaffListing').then((m) => ({ default: m.StaffListing })),
  schools: () => import('@/pages/schools/SchoolListing').then((m) => ({ default: m.SchoolListing })),
  organization: () =>
    import('@/pages/organizations/OrganizationListing').then((m) => ({
      default: m.OrganizationListing,
    })),
  designations: () =>
    import('@/pages/designations/DesignationListing').then((m) => ({
      default: m.DesignationListing,
    })),
  subjects: () => import('@/pages/subjects/SubjectMaster').then((m) => ({ default: m.SubjectMaster })),
  schoolSubjects: () =>
    import('@/pages/subjects/SchoolSubjectAllocation').then((m) => ({
      default: m.SchoolSubjectAllocation,
    })),
  allocations: () =>
    import('@/pages/allocations/SubjectAllocation').then((m) => ({ default: m.SubjectAllocation })),
  classes: () => import('@/pages/classes/ClassListing').then((m) => ({ default: m.ClassListing })),
  sessions: () =>
    import('@/pages/sessions/SessionListing').then((m) => ({ default: m.SessionListing })),
  setup: () => import('@/pages/setup/SetupWizard').then((m) => ({ default: m.SetupWizard })),
  exams: () => import('@/pages/exams/ExamListing').then((m) => ({ default: m.ExamListing })),
  marks: () => import('@/pages/marks/MarksEntry').then((m) => ({ default: m.MarksEntry })),
  examSchedule: () =>
    import('@/pages/exams/ExamScheduleBuilder').then((m) => ({ default: m.ExamScheduleBuilder })),
  results: () => import('@/pages/results/ViewResults').then((m) => ({ default: m.ViewResults })),
  attendance: () => import('@/pages/attendance/Attendance').then((m) => ({ default: m.Attendance })),
  homework: () =>
    import('@/pages/homework/HomeworkLedger').then((m) => ({ default: m.HomeworkLedger })),
  homeworkEval: () =>
    import('@/pages/homework/HomeworkEvaluation').then((m) => ({ default: m.HomeworkEvaluation })),
  collection: () =>
    import('@/pages/collection/CollectionDesk').then((m) => ({ default: m.CollectionDesk })),
  feeStructures: () => import('@/pages/fees/FeeStructures').then((m) => ({ default: m.FeeStructures })),
  invoices: () =>
    import('@/pages/invoices/InvoiceLedger').then((m) => ({ default: m.InvoiceLedger })),
  transactions: () =>
    import('@/pages/transactions/TransactionLedger').then((m) => ({ default: m.TransactionLedger })),
  expenses: () =>
    import('@/pages/expenses/ExpenseLedger').then((m) => ({ default: m.ExpenseLedger })),
  noticeboard: () =>
    import('@/pages/noticeboard/NoticeLedger').then((m) => ({ default: m.NoticeLedger })),
  tasks: () => import('@/pages/tasks/TaskListing').then((m) => ({ default: m.TaskListing })),
  holidays: () =>
    import('@/pages/holidays/HolidayLedger').then((m) => ({ default: m.HolidayLedger })),
  gatePass: () =>
    import('@/pages/gatepass/GatePassRegister').then((m) => ({ default: m.GatePassRegister })),
  timetable: () =>
    import('@/pages/timetable/TimetableBuilder').then((m) => ({ default: m.TimetableBuilder })),
  expenseCategories: () =>
    import('@/pages/expenses/ExpenseCategoryListing').then((m) => ({
      default: m.ExpenseCategoryListing,
    })),
  studentDiscounts: () =>
    import('@/pages/fees/StudentDiscounts').then((m) => ({ default: m.StudentDiscounts })),
  fleet: () =>
    import('@/pages/transport/FleetManagement').then((m) => ({ default: m.FleetManagement })),
  routeBuilder: () =>
    import('@/pages/transport/RouteBuilder').then((m) => ({ default: m.RouteBuilder })),
  passengerAlloc: () =>
    import('@/pages/transport/PassengerAllocations').then((m) => ({
      default: m.PassengerAllocations,
    })),
};

/** Nav id → the component the router renders. */
export const screens: Record<string, React.LazyExoticComponent<() => React.JSX.Element>> =
  Object.fromEntries(Object.entries(loaders).map(([id, load]) => [id, lazy(load)] as const));

/**
 * Starts a screen's download without rendering it.
 *
 * Safe to call as often as the pointer moves: `import()` is memoised by the
 * module registry, so every call after the first resolves from cache. An
 * unknown id — a nav entry not yet ported — is a no-op.
 *
 * Failures are swallowed deliberately. This is speculative work the user did
 * not ask for; if the chunk really is unreachable, the navigation that follows
 * will ask for it again and report it there, where there is a screen to report
 * it on.
 */
export function prefetchScreen(id: string): void {
  void loaders[id]?.().catch(() => undefined);
}
